import React, { useState, useEffect, useMemo } from 'react';
import { useTimeClock } from '../hooks/useTimeClock';
import { NotificationSettings, EmployeeRole } from '../../shared/types.js';
import Button from './Button';
import { cloneDeep } from '../utils.js';

const Fieldset: React.FC<{ legend: string; children: React.ReactNode; className?: string }> = ({ legend, children, className }) => (
    <div className={`border border-gray-200 dark:border-gray-700 rounded-lg p-4 pt-2 ${className}`}>
        <legend className="px-2 font-semibold text-gray-700 dark:text-gray-200 text-lg">{legend}</legend>
        <div className="pt-2 space-y-3">{children}</div>
    </div>
);

const Checkbox: React.FC<{
    label: string;
    checked: boolean;
    onChange: (checked: boolean) => void;
    disabled?: boolean;
}> = ({ label, checked, onChange, disabled }) => (
    <label className={`flex items-center gap-2 ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
        <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} disabled={disabled} className="w-4 h-4 bg-gray-200 dark:bg-gray-600 border-gray-300 dark:border-gray-500 rounded text-brand-secondary focus:ring-brand-secondary"/>
        <span>{label}</span>
    </label>
);

const NotificationSettingsTab: React.FC = () => {
    const { notificationSettings, updateNotificationSettings, smtpSettings, employees } = useTimeClock();
    const [localSettings, setLocalSettings] = useState<NotificationSettings>(cloneDeep(notificationSettings));
    const isSmtpConfigured = useMemo(() => {
        return !!(smtpSettings?.host && smtpSettings?.port && smtpSettings?.fromAddress);
    }, [smtpSettings]);
    const admins = useMemo(() => 
        employees
            .filter(e => e.role === EmployeeRole.ADMIN)
            .sort((a, b) => (a.lastName || '').localeCompare(b.lastName || '') || (a.firstName || '').localeCompare(b.firstName || '')), 
        [employees]
    );
    useEffect(() => {
        setLocalSettings(cloneDeep(notificationSettings));
    }, [notificationSettings]);
    useEffect(() => {
        if (!isSmtpConfigured && localSettings.notifyOnMissedPunch) {
            setLocalSettings(prev => ({ ...prev, notifyOnMissedPunch: false }));
        }
    }, [isSmtpConfigured, localSettings.notifyOnMissedPunch]);
    const handleUpdate = <K extends keyof NotificationSettings>(key: K, value: NotificationSettings[K]) => {
        setLocalSettings(prev => ({ ...prev, [key]: value }));
    };
    const handleAdminIdToggle = (adminId: string) => {
        setLocalSettings(prev => {
            const currentIds = prev.adminIdsToNotify || [];
            const newIds = currentIds.includes(adminId)
                ? currentIds.filter(id => id !== adminId)
                : [...currentIds, adminId];
            return { ...prev, adminIdsToNotify: newIds };
        });
    };
    const isDirty = useMemo(() => {
        return JSON.stringify(notificationSettings) !== JSON.stringify(localSettings);
    }, [notificationSettings, localSettings]);
    const handleApply = () => {
        updateNotificationSettings(localSettings);
    };
    const handleRevert = () => {
        setLocalSettings(cloneDeep(notificationSettings));
    };
    return (
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg max-w-2xl mx-auto text-gray-900 dark:text-white">
            <div className="space-y-8">
                {!isSmtpConfigured && (
                    <div className="p-4 bg-yellow-50 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300 border border-yellow-300 dark:border-yellow-700 rounded-md">
                        <p className="font-bold">SMTP Not Configured</p>
                        <p className="text-sm">Please configure SMTP settings in the "SMTP" tab to enable email notifications.</p>
                    </div>
                )}
                <Fieldset legend="Missed Punch Out">
                    <Checkbox 
                        label="Send email notification on auto-clock-out" 
                        checked={localSettings.notifyOnMissedPunch} 
                        onChange={(v) => handleUpdate('notifyOnMissedPunch', v)}
                        disabled={!isSmtpConfigured}
                    />
                    <div className={`pl-6 space-y-2 ${!localSettings.notifyOnMissedPunch || !isSmtpConfigured ? 'opacity-50' : ''}`}>
                         <Checkbox 
                            label="CC the employee's manager(s)" 
                            checked={localSettings.ccManagers ?? true} 
                            onChange={(v) => handleUpdate('ccManagers', v)}
                            disabled={!localSettings.notifyOnMissedPunch || !isSmtpConfigured}
                        />
                        <div>
                            <p className="font-medium text-gray-900 dark:text-white mt-2">CC administrators:</p>
                            <div className="mt-1 space-y-1 max-h-40 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-md p-2">
                                {admins.map(admin => (
                                    <Checkbox
                                        key={admin.id}
                                        label={`${admin.firstName} ${admin.lastName}`}
                                        checked={(localSettings.adminIdsToNotify || []).includes(admin.id)}
                                        onChange={() => handleAdminIdToggle(admin.id)}
                                        disabled={!localSettings.notifyOnMissedPunch || !isSmtpConfigured}
                                    />
                                ))}
                                {admins.length === 0 && <p className="text-sm text-gray-500 dark:text-gray-400 p-2">No administrators found.</p>}
                            </div>
                        </div>
                    </div>
                     <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        Requires a configured shift with an end time and auto-clock-out enabled.
                    </p>
                </Fieldset>
            </div>
            <div className="flex justify-end gap-4 pt-6 mt-6 border-t border-gray-200 dark:border-gray-700">
                <Button variant="secondary" onClick={handleRevert} disabled={!isDirty}>Revert</Button>
                <Button onClick={handleApply} disabled={!isDirty}>Apply</Button>
            </div>
        </div>
    );
};

export default NotificationSettingsTab;